import { create } from 'zustand';
import type { Message } from '../types/chat';
import { generateUUID } from '../utils/uuid';
import { saveConversation } from './useConversationStorage';
import { useCustomTexts } from './useCustomTexts';
import { useChatbotLimits } from './useChatbotLimits';

// Extend Window interface to add saveTimeout property
declare global {
  interface Window {
    saveTimeout?: NodeJS.Timeout;
  }
}

// Étendre le type Message pour inclure un champ enrichedHtml facultatif
interface EnhancedMessage extends Message {
  enrichedHtml?: string;
  hasProducts?: boolean;
}

interface ChatMessagesStore {
  messages: EnhancedMessage[];
  addMessage: (message: Omit<Message, 'id' | 'timestamp'>) => void;
  updateLastBotMessage: (content: string, enrichedHtml?: string, hasProducts?: boolean) => void;
  setMessages: (messages: EnhancedMessage[]) => void;
  resetMessages: () => void;
  isConversationStarted: boolean;
  setChatbotPublicId: (id: string | null) => void;
  chatbotPublicId: string | null;
  isLimitReached: boolean;
  setLimitReached: (reached: boolean) => void;
}

export const useChatMessages = create<ChatMessagesStore>((set, get) => {
  const getInitialMessage = () => {
    const { welcomeMessage } = useCustomTexts.getState();
    return [{
      id: generateUUID(),
      type: 'bot',
      content: welcomeMessage,
      timestamp: new Date().toISOString()
    }] as EnhancedMessage[];
  };

  return {
    messages: getInitialMessage(),
    isConversationStarted: false,
    chatbotPublicId: null,
    isLimitReached: false,
    
    setChatbotPublicId: (id) => set({ chatbotPublicId: id }),
    
    setLimitReached: (reached) => set({ isLimitReached: reached }),
    
    addMessage: (message) => set((state) => {
      // Check if message limit has been reached
      const { messageLimit } = useChatbotLimits.getState();
      const userMessageCount = state.messages.filter(msg => msg.type === 'user').length;
      
      // If this is a user message and we've reached the limit, don't add it and set limit reached flag
      if (message.type === 'user' && userMessageCount >= messageLimit) {
        return { isLimitReached: true };
      }
      
      const newMessage = {
        id: generateUUID(),
        timestamp: new Date().toISOString(),
        ...message
      };
      const newMessages = [...state.messages, newMessage];
      
      // Marquer le début d'une conversation quand l'utilisateur envoie son premier message
      const isConversationStarted = message.type === 'user' || state.isConversationStarted;
      
      // Sauvegarder la conversation si elle a commencé
      if (isConversationStarted && state.chatbotPublicId) {
        // Utiliser un timeout pour éviter les sauvegardes trop fréquentes
        if (window.saveTimeout) {
          clearTimeout(window.saveTimeout);
        }
        window.saveTimeout = setTimeout(() => {
          // Enregistrer seulement le contenu brut, pas le HTML enrichi
          const plainMessages = newMessages.map(msg => ({
            id: msg.id,
            type: msg.type,
            content: msg.content,
            timestamp: msg.timestamp
          }));
          saveConversation(plainMessages, state.chatbotPublicId!);
        }, 1000);
      }
      
      return { 
        messages: newMessages,
        isConversationStarted,
        isLimitReached: false // Reset limit flag when a message is successfully added
      };
    }),
    updateLastBotMessage: (content, enrichedHtml, hasProducts) => set((state) => {
      const messages = [...state.messages];
      const lastMessage = messages[messages.length - 1];
      if (lastMessage && lastMessage.type === 'bot') {
        messages[messages.length - 1] = {
          ...lastMessage,
          content,
          // Ajouter le HTML enrichi si fourni
          ...(enrichedHtml && { enrichedHtml }),
          // Indiquer si le message contient des produits
          ...(hasProducts !== undefined && { hasProducts })
        };
        
        // Sauvegarder la conversation mise à jour
        if (state.isConversationStarted && state.chatbotPublicId) {
          // Utiliser un timeout pour éviter les sauvegardes trop fréquentes
          if (window.saveTimeout) {
            clearTimeout(window.saveTimeout);
          }
          window.saveTimeout = setTimeout(() => {
            // Enregistrer seulement le contenu brut, pas le HTML enrichi
            const plainMessages = messages.map(msg => ({
              id: msg.id,
              type: msg.type,
              content: msg.content,
              timestamp: msg.timestamp
            }));
            saveConversation(plainMessages, state.chatbotPublicId!);
          }, 1000);
        }
      }
      return { messages };
    }),
    setMessages: (messages) => set({ messages }),
    resetMessages: () => set({ 
      messages: getInitialMessage(),
      isConversationStarted: false,
      isLimitReached: false
    })
  };
});

// Écouter les changements du message de bienvenue
useCustomTexts.subscribe((state, prevState) => {
  if (state.welcomeMessage !== prevState.welcomeMessage) {
    useChatMessages.getState().resetMessages();
  }
});